import { useState, useEffect, useCallback } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { Star, Loader2, MessageSquare, Eye, X } from 'lucide-react'
import { getSurveyNps, type SurveyNpsAnalytics } from '../../../lib/api'
import { AnalyticsNav } from './AnalyticsOverview'

// Funnel actions in display order, with friendly labels.
const FUNNEL: { key: string; label: string }[] = [
  { key: 'seen', label: 'Seen' },
  { key: 'take_survey', label: 'Took survey' },
  { key: 'done', label: "Marked “I've done it”" },
  { key: 'maybe_later', label: 'Maybe later' },
  { key: 'close_x', label: 'Closed (X)' },
]
const EVENT_LABEL: Record<string, string> = Object.fromEntries(FUNNEL.map((f) => [f.key, f.label]))

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function StarRow({ value }: { value: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={
            i <= value ? 'w-3.5 h-3.5 fill-amber-400 text-amber-400' : 'w-3.5 h-3.5 text-muted-foreground/40'
          }
        />
      ))}
    </span>
  )
}

export default function SurveyResults() {
  const [data, setData] = useState<SurveyNpsAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // null = all epochs; a number scopes count/average/distribution to one broadcast.
  const [epoch, setEpoch] = useState<number | null>(null)
  // Funnel action the admin drilled into (clicked its stat box); null = no modal.
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setData(await getSurveyNps(epoch ?? undefined))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load survey ratings')
    } finally {
      setLoading(false)
    }
  }, [epoch])

  useEffect(() => {
    load()
  }, [load])

  const maxDist = data
    ? Math.max(...Object.values(data.distribution), 1)
    : 1

  // People behind the drilled-into funnel action (events are already scoped to
  // the selected broadcast server-side).
  const selectedEventPeople =
    data && selectedEvent ? data.events.filter((e) => e.event === selectedEvent) : []

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2 flex items-center gap-2">
          <Star className="w-5 h-5 text-amber-400" />
          Survey Ratings
        </h1>
        <p className="text-muted-foreground">
          Inline 1–5 star ratings captured by the survey-invite announcement.
          Each &quot;broadcast to all&quot; is a separate epoch; epoch 0 holds
          pre-broadcast self-test previews.
        </p>
      </div>

      <AnalyticsNav />

      {/* Epoch filter */}
      {data && data.by_epoch.length > 0 && (
        <div className="mb-6 flex items-center gap-3">
          <label className="text-sm text-muted-foreground">Broadcast</label>
          <select
            value={epoch ?? ''}
            onChange={(e) => setEpoch(e.target.value === '' ? null : Number(e.target.value))}
            className="rounded-md border border-border bg-card px-2 py-1.5 text-sm"
          >
            <option value="">All broadcasts</option>
            {data.by_epoch.map((b) => (
              <option key={b.epoch} value={b.epoch}>
                {b.epoch === 0 ? 'Epoch 0 (test)' : `Epoch ${b.epoch}`} — {b.count} rating
                {b.count === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading...
        </div>
      ) : error ? (
        <p className="text-red-400">{error}</p>
      ) : !data || (data.count === 0 && data.events.length === 0) ? (
        <p className="text-muted-foreground">
          No activity yet. Stats appear here once the announcement has been shown to users.
        </p>
      ) : (
        <>
          {/* Modal funnel */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <h2 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              Modal funnel
            </h2>
            <p className="text-xs text-muted-foreground mb-4">
              Distinct users per action {epoch == null ? '(all broadcasts)' : `(broadcast #${epoch})`}.
              Each user is counted once per action per broadcast.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              {FUNNEL.map((f) => {
                const n = data.event_counts[f.key] ?? 0
                const seen = data.event_counts.seen ?? 0
                const pct = f.key !== 'seen' && seen > 0 ? Math.round((n / seen) * 100) : null
                const clickable = n > 0
                return (
                  <button
                    key={f.key}
                    type="button"
                    disabled={!clickable}
                    onClick={() => clickable && setSelectedEvent(f.key)}
                    className={`text-left rounded-lg border border-border bg-background p-3 transition-colors ${
                      clickable
                        ? 'cursor-pointer hover:border-amber-500/60 hover:bg-amber-500/5'
                        : 'cursor-default opacity-80'
                    }`}
                  >
                    <div className="text-xs text-muted-foreground">{f.label}</div>
                    <div className="text-2xl font-bold tabular-nums">{n}</div>
                    {pct != null ? (
                      <div className="text-xs text-muted-foreground">{pct}% of seen</div>
                    ) : null}
                  </button>
                )
              })}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">Click a number to see who.</p>
          </div>

          {/* Drill-in: who performed the clicked funnel action. */}
          <Dialog.Root open={selectedEvent !== null} onOpenChange={(o) => !o && setSelectedEvent(null)}>
            <Dialog.Portal>
              <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
              <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[92vw] max-w-lg max-h-[88vh] overflow-y-auto rounded-xl border border-border bg-card p-6 shadow-xl">
                <div className="flex items-center justify-between mb-1">
                  <Dialog.Title className="text-lg font-semibold">
                    {selectedEvent ? (EVENT_LABEL[selectedEvent] ?? selectedEvent) : ''}
                  </Dialog.Title>
                  <Dialog.Close className="text-muted-foreground hover:text-foreground">
                    <X className="w-5 h-5" />
                  </Dialog.Close>
                </div>
                <Dialog.Description className="text-sm text-muted-foreground mb-4">
                  {selectedEventPeople.length} user{selectedEventPeople.length === 1 ? '' : 's'}{' '}
                  {epoch == null ? '(all broadcasts)' : `(broadcast #${epoch})`}
                </Dialog.Description>
                {selectedEventPeople.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No users yet.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-secondary/30 text-xs text-muted-foreground uppercase tracking-wider">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">User</th>
                        <th className="text-left px-3 py-2 font-medium">When</th>
                        <th className="text-right px-3 py-2 font-medium">Broadcast</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEventPeople.map((e, i) => (
                        <tr key={i} className="border-t border-border/50">
                          <td className="px-3 py-2">
                            <div className="font-medium">{e.name || e.email}</div>
                            {e.name ? <div className="text-xs text-muted-foreground">{e.email}</div> : null}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                            {formatDateTime(e.created_at)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                            {e.epoch === 0 ? 'test' : `#${e.epoch}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>

          {data.count === 0 ? (
            <p className="text-muted-foreground mb-8">
              No star ratings submitted yet.
            </p>
          ) : (
          <>
          {/* Headline stats */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-sm text-muted-foreground mb-1">Average rating</div>
              <div className="flex items-center gap-3">
                <span className="text-4xl font-bold tabular-nums">{data.average.toFixed(2)}</span>
                <StarRow value={Math.round(data.average)} />
              </div>
            </div>
            <div className="rounded-xl border border-border bg-card p-6">
              <div className="text-sm text-muted-foreground mb-1">Total ratings</div>
              <div className="text-4xl font-bold tabular-nums">{data.count}</div>
            </div>
          </div>

          {/* Distribution */}
          <div className="rounded-xl border border-border bg-card p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">Rating distribution</h2>
            <div className="space-y-2">
              {[5, 4, 3, 2, 1].map((star) => {
                const n = data.distribution[String(star)] ?? 0
                const pct = Math.round((n / maxDist) * 100)
                const share = data.count > 0 ? Math.round((n / data.count) * 100) : 0
                return (
                  <div key={star} className="grid grid-cols-[3rem_1fr_4rem] items-center gap-3">
                    <span className="flex items-center gap-1 text-sm text-muted-foreground">
                      {star} <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                    </span>
                    <span className="relative h-2.5 rounded-full bg-secondary/40 overflow-hidden">
                      <span
                        className="absolute inset-y-0 left-0 rounded-full bg-amber-400/70"
                        style={{ width: `${pct}%` }}
                      />
                    </span>
                    <span className="text-sm tabular-nums text-right text-muted-foreground">
                      {n} ({share}%)
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Individual ratings — attributed to the submitting user */}
          {data.ratings.length > 0 && (
            <div className="rounded-xl border border-border bg-card p-6">
              <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-muted-foreground" />
                Ratings ({data.ratings.length})
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/30 text-xs text-muted-foreground uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">User</th>
                      <th className="text-left px-3 py-2 font-medium">Rating</th>
                      <th className="text-left px-3 py-2 font-medium">Comment</th>
                      <th className="text-left px-3 py-2 font-medium">When</th>
                      <th className="text-right px-3 py-2 font-medium">Broadcast</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.ratings.map((r, i) => (
                      <tr key={i} className="border-t border-border/50 hover:bg-secondary/20 align-top">
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.name || r.email}</div>
                          {r.name ? (
                            <div className="text-xs text-muted-foreground">{r.email}</div>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <StarRow value={r.rating} />
                        </td>
                        <td className="px-3 py-2 text-muted-foreground">{r.comment || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">
                          {formatDateTime(r.created_at)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                          {r.epoch === 0 ? 'test' : `#${r.epoch}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          </>
          )}
        </>
      )}
    </div>
  )
}
